import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Ban,
  Check,
  ClipboardList,
  Download,
  Info,
  PackageCheck,
  Plus,
  RotateCcw,
  } from 'lucide-react';
import {
  RECEIVING_DIVERGENCE_STATUS_LABELS,
  RECEIVING_DIVERGENCE_TYPE_LABELS,
  RECEIVING_ITEM_CONDITION_LABELS,
  RECEIVING_SITUATION_LABELS,
  RECEIVING_SITUATIONS,
  RECEIVING_STOCK_STATUS_LABELS,
  RECEIVING_STOCK_STATUSES,
  SERVICE_ACCEPTANCE_LABELS,
  formatCents,
  formatQty,
  type Paginated,
  type ReceivableOrder,
  type Receiving,
  type ReceivingSummary,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatDate, formatDateTime, formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SummaryCards } from '@/components/SummaryCards';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { Attachments } from '@/components/Attachments';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, LoadingState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useCostCenters, useStockLocations, useSuppliers } from '@/lib/queries';
import { DIVERGENCE_TONE, SITUATION_TONE, STOCK_TONE, conferenciaPath } from './recebimentos-shared';

/**
 * Recebimentos — a entrada física dos materiais e o aceite dos serviços.
 *
 * O recebimento nasce de um pedido EMITIDO e é conferido item a item numa
 * página própria: quanto chegou, quanto foi aceito, quanto foi recusado. Só o
 * ACEITO entra no estoque, e isso acontece na conclusão, numa transação só
 * com o pedido e a requisição. Divergência é registro, com ciclo e resolução,
 * não observação perdida num campo de texto.
 */
export function RecebimentosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const suppliers = useSuppliers();
  const costCenters = useCostCenters();
  const locations = useStockLocations();

  const [search, setSearch] = useState('');
  const [situation, setSituation] = useState('');
  const [personId, setPersonId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [stockStatus, setStockStatus] = useState('');
  const [hasDivergence, setHasDivergence] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [checked, setChecked] = useState<string[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [confirmando, setConfirmando] = useState<'conclude' | 'cancel' | 'reverse' | null>(null);

  const filtros = {
    search: search || undefined,
    situation: situation || undefined,
    personId: personId || undefined,
    costCenterId: costCenterId || undefined,
    locationId: locationId || undefined,
    stockStatus: stockStatus || undefined,
    hasDivergence: hasDivergence || undefined,
    from: from || undefined,
    to: to || undefined,
  };

  const listQuery = useQuery({
    queryKey: ['receivings', { ...filtros, page, perPage }],
    queryFn: () => api.get<Paginated<Receiving>>('/receivings', { ...filtros, page, perPage }),
  });

  const summaryQuery = useQuery({
    queryKey: ['receivings', 'summary', filtros],
    queryFn: () => api.get<ReceivingSummary>('/receivings/summary', filtros),
  });

  const detailQuery = useQuery({
    queryKey: ['receiving', selectedId],
    queryFn: () => api.get<Receiving>(`/receivings/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['receivings'] });
    void queryClient.invalidateQueries({ queryKey: ['receiving'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-balances'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const concludeMutation = useMutation({
    mutationFn: () => api.post(`/receivings/${selectedId}/conclude`, {}),
    onSuccess: () => {
      setConfirmando(null);
      refresh();
    },
  });
  const cancelMutation = useMutation({
    mutationFn: (reason: string) => api.post(`/receivings/${selectedId}/cancel`, { reason }),
    onSuccess: () => {
      setConfirmando(null);
      refresh();
    },
  });

  const receiving = detailQuery.data;

  const maisFiltrosAtivos = [stockStatus, hasDivergence].filter(Boolean).length;
  const temFiltro =
    Boolean(search || situation || personId || costCenterId || locationId || from || to) || maisFiltrosAtivos > 0;

  const limparMais = () => {
    setStockStatus('');
    setHasDivergence('');
    setPage(1);
  };
  const limparTudo = () => {
    setSearch('');
    setSituation('');
    setPersonId('');
    setCostCenterId('');
    setLocationId('');
    setFrom('');
    setTo('');
    limparMais();
  };

  const alternaSituacao = (valor: string) => {
    setSituation(situation === valor ? '' : valor);
    setPage(1);
  };

  const resumo = summaryQuery.data;
  const cards = [
    {
      key: 'recebidos',
      label: 'Recebidos',
      value: String(resumo?.received ?? 0),
      hint: 'Entraram no estoque',
      tone: 'ok' as const,
      active: situation === 'RECEBIDO',
      onClick: () => alternaSituacao('RECEBIDO'),
    },
    {
      key: 'conferencia',
      label: 'Em conferência',
      value: String(resumo?.inProgress ?? 0),
      hint: 'Aguardando finalização',
      tone: 'atencao' as const,
      active: situation === 'EM_CONFERENCIA',
      onClick: () => alternaSituacao('EM_CONFERENCIA'),
    },
    {
      key: 'parciais',
      label: 'Parciais',
      value: String(resumo?.partial ?? 0),
      hint: 'Com itens pendentes',
      tone: 'info' as const,
      active: situation === 'PARCIAL',
      onClick: () => alternaSituacao('PARCIAL'),
    },
    {
      key: 'divergencia',
      label: 'Com divergência',
      value: String(resumo?.withDivergence ?? 0),
      hint: 'Aguardando análise',
      tone: 'ruim' as const,
      active: situation === 'COM_DIVERGENCIA',
      onClick: () => alternaSituacao('COM_DIVERGENCIA'),
    },
    {
      key: 'valor',
      label: 'Valor recebido (período)',
      value: formatCents(resumo?.totalCents ?? 0),
      hint: from || to ? 'No período filtrado' : 'Recebimentos concluídos',
      tone: 'neutro' as const,
    },
  ];

  const columns: Array<Column<Receiving>> = [
    { key: 'code', label: 'Número', strong: true, mono: true, width: 150, render: (row) => row.code },
    { key: 'receivedAt', label: 'Data', width: 100, render: (row) => formatDate(row.receivedAt) },
    { key: 'order', label: 'Pedido', mono: true, width: 150, render: (row) => blank(row.purchaseOrderCode) },
    {
      key: 'supplier',
      label: 'Fornecedor',
      render: (row) => (
        <div>
          <div>{blank(row.supplierName)}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            {row.supplierDocument ? formatDocument(row.supplierDocument) : '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'document',
      label: 'NF-e',
      mono: true,
      width: 110,
      render: (row) => (row.documentNumber ? row.documentNumber : <span className="muted">—</span>),
    },
    {
      key: 'items',
      label: 'Itens',
      width: 80,
      render: (row) => `${row.itemCount} ${row.itemCount === 1 ? 'item' : 'itens'}`,
    },
    {
      key: 'totalCents',
      label: 'Valor recebido',
      right: true,
      mono: true,
      width: 130,
      render: (row) => formatCents(row.totalCents),
    },
    {
      key: 'situation',
      label: 'Situação',
      width: 140,
      render: (row) => (
        <StatusBadge kind={SITUATION_TONE[row.situation]}>{RECEIVING_SITUATION_LABELS[row.situation]}</StatusBadge>
      ),
    },
    {
      key: 'stock',
      label: 'Estoque',
      width: 120,
      render: (row) =>
        row.status === 'CANCELADO' && row.stockStatus !== 'ESTORNADO' ? (
          <span className="muted">—</span>
        ) : (
          <StatusBadge kind={STOCK_TONE[row.stockStatus]}>{RECEIVING_STOCK_STATUS_LABELS[row.stockStatus]}</StatusBadge>
        ),
    },
    { key: 'responsible', label: 'Responsável', width: 140, render: (row) => blank(row.receivedByName) },
  ];

  const exportar = () => {
    const linhas = (listQuery.data?.data ?? []).map((row) =>
      [
        row.code,
        formatDate(row.receivedAt),
        row.purchaseOrderCode ?? '',
        row.supplierName ?? '',
        row.supplierDocument ?? '',
        row.documentNumber ?? '',
        String(row.itemCount),
        (row.totalCents / 100).toFixed(2).replace('.', ','),
        RECEIVING_SITUATION_LABELS[row.situation],
        RECEIVING_STOCK_STATUS_LABELS[row.stockStatus],
        row.receivedByName ?? '',
      ].join(';'),
    );
    const csv = [
      'Número;Data;Pedido;Fornecedor;CNPJ;NF-e;Itens;Valor recebido;Situação;Estoque;Responsável',
      ...linhas,
    ].join('\n');
    // BOM para o Excel brasileiro abrir com acento certo.
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recebimentos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const aberto = receiving && (receiving.status === 'RASCUNHO' || receiving.status === 'EM_CONFERENCIA');

  return (
    <>
      <WorkspaceHeader
        title="Recebimentos"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" icon={Download} onClick={exportar} disabled={!listQuery.data?.data.length}>
              Exportar
            </Button>
            {can('recebimentos:criar') && (
              <Button variant="primary" icon={Plus} onClick={() => setNovo(true)}>
                Novo recebimento
              </Button>
            )}
          </div>
        }
      />

      <div className="workspace__body">
        <SummaryCards cards={cards} loading={summaryQuery.isPending} />

        <FilterBar
          placeholder="Buscar por número, pedido, fornecedor, NF-e ou item…"
          search={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          filters={[
            {
              key: 'situation',
              label: 'Situação',
              value: situation,
              onChange: (v) => {
                setSituation(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Todos' },
                ...RECEIVING_SITUATIONS.map((s) => ({ value: s, label: RECEIVING_SITUATION_LABELS[s] })),
              ],
            },
            {
              key: 'person',
              label: 'Fornecedor',
              value: personId,
              onChange: (v) => {
                setPersonId(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Todos' },
                ...(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.tradeName || s.name })),
              ],
            },
            {
              key: 'costCenter',
              label: 'Centro de custo',
              value: costCenterId,
              onChange: (v) => {
                setCostCenterId(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Todos' },
                ...(costCenters.data ?? []).map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` })),
              ],
            },
            {
              key: 'location',
              label: 'Local',
              value: locationId,
              onChange: (v) => {
                setLocationId(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Todos' },
                ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
              ],
            },
          ]}
          from={from}
          to={to}
          onFromChange={(v) => {
            setFrom(v);
            setPage(1);
          }}
          onToChange={(v) => {
            setTo(v);
            setPage(1);
          }}
          activeMoreFilters={maisFiltrosAtivos}
          onClearMore={limparMais}
          moreFilters={
            <>
              <label>
                <span>Estoque</span>
                <select
                  value={stockStatus}
                  onChange={(e) => {
                    setStockStatus(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  {RECEIVING_STOCK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {RECEIVING_STOCK_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Divergência</span>
                <select
                  value={hasDivergence}
                  onChange={(e) => {
                    setHasDivergence(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  <option value="true">Só com divergência em aberto</option>
                  <option value="false">Só sem divergência</option>
                </select>
              </label>
            </>
          }
        />

        {listQuery.isPending ? (
          <TableSkeleton />
        ) : listQuery.isError ? (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        ) : (
          <SmartDataTable
            caption="Recebimentos"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
            checkedIds={checked}
            onCheckedChange={setChecked}
            rowActions={(row) =>
              row.status === 'RASCUNHO' || row.status === 'EM_CONFERENCIA' ? (
                <button
                  type="button"
                  title="Continuar a conferência"
                  onClick={(e) => {
                    e.stopPropagation();
                    void navigate({ to: conferenciaPath(row.id) });
                  }}
                  style={{ display: 'inline-flex', padding: 4, color: T.mareDark, background: 'none', border: 0, cursor: 'pointer' }}
                >
                  <ClipboardList size={15} />
                </button>
              ) : null
            }
            emptyState={
              temFiltro ? (
                <NoResultsState onClear={limparTudo} />
              ) : (
                <EmptyState
                  title="Nenhum recebimento"
                  description="Quando o material chegar, abra um recebimento a partir do pedido emitido e confira item a item — o estoque só entra depois da conferência."
                  action={
                    can('recebimentos:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setNovo(true)}>
                        Novo recebimento
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}

        <div className="note">
          <Info size={16} className="note__icon" aria-hidden />
          <div>
            <strong>O recebimento deve ser conferido com base no pedido de compra.</strong>{' '}
            Em caso de divergência (falta, sobra, avaria ou item incorreto), registre as observações, anexe fotos e
            informe o responsável. O estoque só é atualizado com a quantidade aceita, depois da conferência.
          </div>
        </div>
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={receiving?.code ?? 'Recebimento'}
        subtitle={receiving ? `${receiving.purchaseOrderCode ?? ''} · ${receiving.supplierName ?? ''}` : undefined}
        tabs={['Resumo', 'Itens', 'Divergências', 'Documentos', 'Estoque', 'Origem', 'Histórico']}
        footer={
          receiving && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {aberto && can('recebimentos:editar') && (
                <Button icon={ClipboardList} onClick={() => void navigate({ to: conferenciaPath(receiving.id) })}>
                  Continuar conferência
                </Button>
              )}
              {receiving.status === 'EM_CONFERENCIA' && can('recebimentos:concluir') && (
                <Button variant="ghost" icon={Check} onClick={() => setConfirmando('conclude')}>
                  Concluir
                </Button>
              )}
              {aberto && can('recebimentos:editar') && (
                <Button variant="ghost" icon={Ban} onClick={() => setConfirmando('cancel')}>
                  Cancelar
                </Button>
              )}
              {receiving.status === 'CONCLUIDO' && can('recebimentos:estornar') && (
                <Button variant="ghost" icon={RotateCcw} onClick={() => setConfirmando('reverse')}>
                  Estornar
                </Button>
              )}
            </div>
          )
        }
      >
        {(aba) => {
          if (detailQuery.isPending) return <LoadingState label="Abrindo o recebimento…" />;
          if (detailQuery.isError) return <ErrorState message={errorMessage(detailQuery.error)} />;
          if (!receiving) return null;

          if (aba === 1) return <AbaItens receiving={receiving} />;
          if (aba === 2) return <AbaDivergencias receiving={receiving} />;
          if (aba === 3)
            return <Attachments entity="Receiving" entityId={receiving.id} canEdit={can('recebimentos:editar')} />;
          if (aba === 4) return <AbaEstoque receiving={receiving} />;
          if (aba === 5) return <AbaOrigem receiving={receiving} />;
          if (aba === 6) return <AbaHistorico receivingId={receiving.id} />;
          return <AbaResumo receiving={receiving} />;
        }}
      </ContextPanel>

      {novo && (
        <NovoRecebimentoDrawer
          onClose={() => setNovo(false)}
          onCreated={(created) => {
            setNovo(false);
            refresh();
            void navigate({ to: conferenciaPath(created.id) });
          }}
        />
      )}

      <ConfirmDialog
        open={confirmando !== null}
        title={
          confirmando === 'conclude'
            ? 'Concluir o recebimento'
            : confirmando === 'reverse'
              ? 'Estornar o recebimento'
              : 'Cancelar a conferência'
        }
        message={
          confirmando === 'conclude'
            ? 'A quantidade ACEITA de cada linha entra no estoque agora, o pedido abate o recebido e a requisição vira atendida. Depois disso, desfazer é estorno.'
            : confirmando === 'reverse'
              ? 'Cada entrada de estoque ganha um contra-movimento, o pedido e a requisição devolvem o saldo, e o recebimento fica cancelado com o motivo. Nada é apagado. Se o material já foi consumido, o estorno é recusado.'
              : 'A conferência é descartada e o pedido volta a aceitar um recebimento novo. Nada entrou no estoque.'
        }
        confirmLabel={confirmando === 'conclude' ? 'Concluir e dar entrada' : confirmando === 'reverse' ? 'Estornar' : 'Cancelar conferência'}
        destructive={confirmando !== 'conclude'}
        requireReason={confirmando !== 'conclude'}
        loading={concludeMutation.isPending || cancelMutation.isPending}
        error={
          confirmando === 'conclude'
            ? concludeMutation.error
              ? errorMessage(concludeMutation.error)
              : undefined
            : cancelMutation.error
              ? errorMessage(cancelMutation.error)
              : undefined
        }
        onClose={() => setConfirmando(null)}
        onConfirm={(reason) => {
          if (confirmando === 'conclude') concludeMutation.mutate();
          else cancelMutation.mutate(reason ?? '');
        }}
      />
    </>
  );
}

/* ------------------------------ novo recebimento --------------------------- */

/**
 * Escolher o pedido é tudo o que se digita aqui. O resto (fornecedor, itens,
 * saldo, local previsto) vem do pedido; a conferência acontece na página
 * própria, com espaço para muitas linhas.
 */
function NovoRecebimentoDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: (r: Receiving) => void }) {
  const navigate = useNavigate();
  const locations = useStockLocations();
  const [search, setSearch] = useState('');
  const [orderId, setOrderId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');

  const ordersQuery = useQuery({
    queryKey: ['receivings', 'receivable-orders', search],
    queryFn: () => api.get<{ data: ReceivableOrder[] }>('/receivings/receivable-orders', { search: search || undefined }),
    select: (r) => r.data,
  });

  const order = (ordersQuery.data ?? []).find((o) => o.id === orderId);
  const local = locationId || order?.locationId || '';

  const mutation = useMutation({
    mutationFn: () =>
      api.post<Receiving>('/receivings', {
        purchaseOrderId: orderId,
        locationId: local || null,
        documentNumber: documentNumber.trim() || null,
      }),
    onSuccess: onCreated,
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Novo recebimento"
      description="Escolha o pedido: fornecedor, itens e saldo vêm dele"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Voltar
          </Button>
          {order?.openReceivingId ? (
            <Button icon={ClipboardList} onClick={() => void navigate({ to: conferenciaPath(order.openReceivingId!) })}>
              Continuar {order.openReceivingCode}
            </Button>
          ) : (
            <Button
              icon={PackageCheck}
              disabled={!orderId || !local}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Abrir conferência
            </Button>
          )}
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Buscar pedido" htmlFor="novo-rec-busca" hint="Só aparecem pedidos emitidos com saldo a receber.">
          <input
            id="novo-rec-busca"
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="PED-2026-000068 ou fornecedor"
          />
        </Field>

        {ordersQuery.isPending && <LoadingState label="Buscando pedidos…" />}
        {ordersQuery.isError && <ErrorState message={errorMessage(ordersQuery.error)} />}
        {ordersQuery.data && ordersQuery.data.length === 0 && (
          <p className="muted" style={{ fontSize: 12.5 }}>
            Nenhum pedido emitido aguardando entrega. Emita o pedido em Compras › Pedidos.
          </p>
        )}
        {ordersQuery.data && ordersQuery.data.length > 0 && (
          <div role="radiogroup" aria-label="Pedidos elegíveis" style={{ display: 'grid', gap: 8 }}>
            {ordersQuery.data.map((o) => {
              const ativo = o.id === orderId;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={ativo}
                  onClick={() => setOrderId(o.id)}
                  className="card"
                  style={{
                    textAlign: 'left',
                    padding: 12,
                    cursor: 'pointer',
                    borderColor: ativo ? T.mareDark : T.line,
                    background: ativo ? T.mareTint : undefined,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <strong className="mono" style={{ fontSize: 13 }}>
                      {o.code}
                    </strong>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {formatCents(o.totalCents)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, marginTop: 2 }}>{blank(o.supplierName)}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {o.pendingItems} {o.pendingItems === 1 ? 'item pendente' : 'itens pendentes'}
                    {o.expectedAt ? ` · prazo ${formatDate(o.expectedAt)}` : ''}
                    {o.receiving === 'PARCIAL' ? ' · parcialmente recebido' : ''}
                    {o.openReceivingCode ? ` · conferência ${o.openReceivingCode} aberta` : ''}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </FormSection>

      {order && !order.openReceivingId && (
        <FormSection title="Onde está chegando">
          <Field label="Local de recebimento" htmlFor="novo-rec-local" hint="Vem do pedido; troque se a carga foi para outro lugar.">
            <select id="novo-rec-local" className="input" value={local} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Selecione…</option>
              {(locations.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code} · {l.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Número da NF-e / DANFE (opcional)" htmlFor="novo-rec-nf" hint="Se a nota veio junto. O XML entra depois, em Notas de entrada.">
            <input
              id="novo-rec-nf"
              className="input"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder="432689"
            />
          </Field>
        </FormSection>
      )}
    </Drawer>
  );
}

/* -------------------------------- abas ------------------------------------ */

function AbaResumo({ receiving }: { receiving: Receiving }) {
  const aceito = receiving.items.reduce((s, i) => s + i.acceptedQuantityMilli, 0);
  const recusado = receiving.items.reduce((s, i) => s + i.rejectedQuantityMilli, 0);
  return (
    <div>
      <StatTiles
        items={[
          { label: 'Valor recebido', value: formatCents(receiving.totalCents) },
          { label: 'Aceito', value: formatQty(aceito) },
          { label: 'Recusado', value: formatQty(recusado) },
        ]}
      />
      <ReadField
        label="Situação"
        value={
          <StatusBadge kind={SITUATION_TONE[receiving.situation]}>
            {RECEIVING_SITUATION_LABELS[receiving.situation]}
          </StatusBadge>
        }
      />
      <ReadField
        label="Estoque"
        value={
          <StatusBadge kind={STOCK_TONE[receiving.stockStatus]}>
            {RECEIVING_STOCK_STATUS_LABELS[receiving.stockStatus]}
          </StatusBadge>
        }
      />
      <ReadField label="Pedido" value={blank(receiving.purchaseOrderCode)} mono />
      <ReadField label="Fornecedor" value={blank(receiving.supplierName)} />
      <ReadField label="Local" value={blank(receiving.locationName)} />
      <ReadField label="Recebido em" value={formatDateTime(receiving.receivedAt)} mono />
      <ReadField label="Conferente" value={blank(receiving.receivedByName)} />
      {receiving.concludedAt && (
        <ReadField
          label="Concluído"
          value={`${formatDateTime(receiving.concludedAt)} · ${blank(receiving.concludedByName)}`}
        />
      )}
      {receiving.documentNumber && <ReadField label="NF-e / DANFE" value={receiving.documentNumber} mono />}
      {receiving.cancelReason && <ReadField label="Motivo do cancelamento" value={receiving.cancelReason} />}
      {receiving.notes && <ReadField label="Observações" value={receiving.notes} />}
    </div>
  );
}

function AbaItens({ receiving }: { receiving: Receiving }) {
  return (
    <div>
      {receiving.items.map((item) => (
        <div key={item.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{item.description}</div>
            {item.kind === 'SERVICO' ? (
              <StatusBadge kind="info">Serviço</StatusBadge>
            ) : item.condition !== 'OK' ? (
              <StatusBadge kind={item.condition === 'EXCESSO' ? 'warn' : 'bad'}>
                {RECEIVING_ITEM_CONDITION_LABELS[item.condition]}
              </StatusBadge>
            ) : (
              <StatusBadge kind="ok">OK</StatusBadge>
            )}
          </div>
          {item.specification && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
              {item.specification}
            </div>
          )}
          <div className="mono" style={{ fontSize: 12, color: T.inkFaint, marginTop: 6 }}>
            pedido {formatQty(item.orderedQuantityMilli)} · esperado {formatQty(item.expectedQuantityMilli)} · entregue{' '}
            {formatQty(item.deliveredQuantityMilli)} · aceita {formatQty(item.acceptedQuantityMilli)} · recusada{' '}
            {formatQty(item.rejectedQuantityMilli)} {item.unit}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12 }}>
            <span>
              {item.pendingQuantityMilli > 0 ? (
                <span style={{ color: T.amber }}>pendente {formatQty(item.pendingQuantityMilli)} {item.unit}</span>
              ) : (
                <span style={{ color: T.green }}>linha completa</span>
              )}
              {item.serviceStatus ? ` · ${SERVICE_ACCEPTANCE_LABELS[item.serviceStatus]}` : ''}
            </span>
            <span className="mono" style={{ fontWeight: 600 }}>
              {formatCents(item.totalCents)}
            </span>
          </div>
          {item.notes && (
            <div style={{ fontSize: 12, marginTop: 6, color: T.inkSoft }}>{item.notes}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function AbaDivergencias({ receiving }: { receiving: Receiving }) {
  if (receiving.divergences.length === 0) {
    return (
      <EmptyState
        title="Sem divergências"
        description="Falta, excesso, avaria ou item incorreto registrados na conferência aparecem aqui, com o tratamento dado."
      />
    );
  }
  return (
    <div>
      {receiving.divergences.map((d) => (
        <div key={d.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <strong style={{ fontSize: 13 }}>{RECEIVING_DIVERGENCE_TYPE_LABELS[d.type]}</strong>
            <StatusBadge kind={DIVERGENCE_TONE[d.status]}>{RECEIVING_DIVERGENCE_STATUS_LABELS[d.status]}</StatusBadge>
          </div>
          {d.itemDescription && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
              {d.itemDescription}
              {d.quantityMilli > 0 ? ` · ${formatQty(d.quantityMilli)}` : ''}
            </div>
          )}
          <div style={{ fontSize: 12.5, marginTop: 6 }}>{d.description}</div>
          {d.resolution && (
            <div style={{ fontSize: 12, marginTop: 6, color: T.green }}>
              Tratamento: {d.resolution}
              {d.resolvedByName ? ` — ${d.resolvedByName}` : ''}
            </div>
          )}
          <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
            {formatDateTime(d.createdAt)} · {blank(d.createdByName)}
          </div>
        </div>
      ))}
    </div>
  );
}

function AbaEstoque({ receiving }: { receiving: Receiving }) {
  const linhas = receiving.items.filter((i) => i.controlsStock);
  return (
    <div>
      <ReadField
        label="Integração"
        value={
          <StatusBadge kind={STOCK_TONE[receiving.stockStatus]}>
            {RECEIVING_STOCK_STATUS_LABELS[receiving.stockStatus]}
          </StatusBadge>
        }
      />
      {linhas.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5 }}>
          Nenhuma linha deste recebimento movimenta estoque (serviço ou item sem cadastro).
        </p>
      ) : (
        linhas.map((i) => (
          <div key={i.id} className="card" style={{ padding: 10, marginBottom: 8, fontSize: 12.5 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>
                {i.description}
                {i.sku ? <span className="muted"> · {i.sku}</span> : null}
              </span>
              <span className="mono">
                {formatQty(i.acceptedQuantityMilli)} {i.unit}
              </span>
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              {i.stockMovementId ? `entrada registrada · custo ${formatCents(i.unitCostCents)}` : 'entra ao concluir'}
            </div>
          </div>
        ))
      )}
      <p className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
        As entradas aparecem em Estoque › Movimentações com a origem &quot;Recebimento de compra&quot;. O estorno gera o
        contra-movimento no mesmo lugar.
      </p>
    </div>
  );
}

function AbaOrigem({ receiving }: { receiving: Receiving }) {
  const passos = [
    { label: 'Requisição', value: receiving.requisitionCode },
    { label: 'Cotação', value: receiving.quotationCode },
    { label: 'Pedido', value: receiving.purchaseOrderCode },
    { label: 'Recebimento', value: receiving.code },
    { label: 'NF-e', value: receiving.documentNumber },
  ];
  return (
    <div>
      <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {passos.map((p) => (
          <li key={p.label} style={{ borderLeft: `2px solid ${p.value ? T.mareDark : T.line}`, paddingLeft: 12, paddingBottom: 12 }}>
            <div className="muted" style={{ fontSize: 11 }}>
              {p.label}
            </div>
            <div className="mono" style={{ fontSize: 13, color: p.value ? undefined : T.inkFaint }}>
              {p.value ?? '—'}
            </div>
          </li>
        ))}
      </ol>
      <ReadField label="Centro de custo" value={blank(receiving.costCenterName)} />
      {receiving.unitName && <ReadField label="Unidade" value={receiving.unitName} />}
      {receiving.documentKey && <ReadField label="Chave de acesso" value={receiving.documentKey} mono />}
    </div>
  );
}

function AbaHistorico({ receivingId }: { receivingId: string }) {
  const query = useQuery({
    queryKey: ['receiving', receivingId, 'timeline'],
    queryFn: () =>
      api.get<Array<{ id: string; action: string; userName: string | null; at: string; reason: string | null }>>(
        `/receivings/${receivingId}/timeline`,
      ),
  });

  if (query.isPending) return <LoadingState label="Carregando o histórico…" />;
  if (query.isError) return <ErrorState message={errorMessage(query.error)} />;
  if (query.data.length === 0) {
    return <p className="muted" style={{ fontSize: 13 }}>Nada registrado ainda.</p>;
  }

  const rotulo: Record<string, string> = {
    CREATE: 'Recebimento aberto',
    UPDATE: 'Conferência alterada',
    APPROVE: 'Recebimento concluído',
    DELETE: 'Recebimento estornado',
  };

  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {query.data.map((linha) => (
        <li key={linha.id} style={{ borderLeft: `2px solid ${T.line}`, paddingLeft: 12, paddingBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{rotulo[linha.action] ?? linha.action}</div>
          <div style={{ fontSize: 12 }}>{blank(linha.userName)}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            {formatDateTime(linha.at)}
          </div>
          {linha.reason && <div style={{ fontSize: 12, marginTop: 2 }}>{linha.reason}</div>}
        </li>
      ))}
    </ol>
  );
}
